'use client';

/**
 * The two freshness-dependent fragments of the home page's zone grid.
 *
 * They exist as their own Client Components so the home page can be
 * STATICALLY generated again. Carrying a live badge used to cost the whole
 * route: `export const dynamic = 'force-dynamic'` on `/`, `/en` and `/ru`
 * turned every home-page hit into a full server render for one pill, while
 * every sibling page stayed prerendered. Freshness is a browser-side fact
 * here — it arrives on the SSE stream the world store already owns — so only
 * these two spans need to be dynamic, and the page around them does not.
 *
 * Honesty rule, same as everywhere else: the first paint knows nothing, and
 * `displayFreshness` maps that to SYNCING, which claims neither "real" nor
 * "simulated". Only OFFLINE — an observer the site tried and failed to reach
 * — carries the `[SIM]` mark.
 */

import { useWorld } from '../../lab/store';
import { displayFreshness, freshnessSuffix } from '../../lab/freshness-label';
import LiveBadge from './LiveBadge';
import type { Lang } from '../../i18n/config';

const pad = (n: number) => String(n).padStart(2, '0');

export interface AgentsZoneCountProps {
  /** Localized noun for the live/stale/syncing case, e.g. `AGENTS`. */
  agentsWord: string;
  /** Localized noun already carrying the `[SIM]` mark, for the OFFLINE case. */
  agentsSimWord: string;
}

export function AgentsZoneCount({ agentsWord, agentsSimWord }: AgentsZoneCountProps) {
  const agents = useWorld((w) => w.agents);
  const rosterLive = useWorld((w) => w.rosterLive);
  const freshness = useWorld((w) => w.freshness);
  const liveMode = useWorld((w) => w.liveMode);
  const liveStats = useWorld((w) => w.liveStats);
  const displayed = displayFreshness(rosterLive, freshness);
  // The universe's own count when the bridge reported it; the rows on hand
  // otherwise. Never the served roster length dressed up as the population.
  const count = liveStats?.activeAgents ?? agents.length;

  return displayed === 'OFFLINE' ? (
    <>
      {pad(count)} {agentsSimWord}
    </>
  ) : (
    <>
      {pad(count)} {agentsWord} {freshnessSuffix(displayed, liveMode)}
    </>
  );
}

export interface ObservatoryZoneStateProps {
  lang: Lang;
  /** The card's own label — `LIVE STATE` / `ЖИВОЕ СОСТОЯНИЕ`. Never replaced. */
  label: string;
}

/**
 * The Observatory card keeps its own label and gains a badge beside it. The
 * badge used to REPLACE the label (`z.badge ?? z.count`), leaving a bare
 * `OFFLINE` pill on a card whose description says its state is computed from
 * the site's own registries — which is registry-derived and unaffected by the
 * observer either way.
 */
export function ObservatoryZoneState({ lang, label }: ObservatoryZoneStateProps) {
  const rosterLive = useWorld((w) => w.rosterLive);
  const freshness = useWorld((w) => w.freshness);
  const reason = useWorld((w) => w.freshnessReason);
  const liveMode = useWorld((w) => w.liveMode);
  const displayed = displayFreshness(rosterLive, freshness);

  return (
    <>
      {label} <LiveBadge lang={lang} freshness={displayed} reason={reason} mode={liveMode} />
    </>
  );
}
